import { useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Input from '@/ui/Form/Input';
import './AddressAutocomplete.css';

export default function AddressAutocomplete({
  adapter,
  value,
  onChange,
  onSelect,
  error,
  id,
  'aria-describedby': describedBy,
  'aria-invalid': ariaInvalid,
}) {
  // The listbox and option ids; `id` is the input's, from InputRow's label.
  const listId = useId();
  const [focused, setFocused] = useState(false);
  const [hasEdited, setHasEdited] = useState(false);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(-1);
  const resolution = useRef(null);
  useEffect(
    () => () => {
      resolution.current?.abort();
      resolution.current = null;
      adapter.reset?.();
    },
    [adapter],
  );
  useEffect(() => {
    let live = true;
    const controller = new AbortController();
    setActive(-1);
    if (!focused || value.trim().length < 3 || !hasEdited) {
      setItems([]);
      return () => {
        live = false;
        controller.abort();
      };
    }
    const timer = setTimeout(async () => {
      try {
        const results = await adapter.suggest(value.trim(), {
          signal: controller.signal,
        });
        if (live) setItems(results);
      } catch {
        if (live) setItems([]);
      }
    }, 250);
    return () => {
      live = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [value, adapter, focused, hasEdited]);
  const open = focused && items.length > 0;
  // Modal captura Escape en document y cierra el formulario entero. Con la
  // lista abierta, este listener en window corre antes y solo cierra la lista.
  useEffect(() => {
    if (!open) return undefined;
    const closeList = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      event.preventDefault();
      setFocused(false);
    };
    window.addEventListener('keydown', closeList, true);
    return () => window.removeEventListener('keydown', closeList, true);
  }, [open]);
  const select = async (item) => {
    resolution.current?.abort();
    const controller = new AbortController();
    resolution.current = controller;
    onChange(item.address || value);
    setHasEdited(false);
    setFocused(false);
    let selected = item;
    try {
      if (adapter.resolve)
        selected = await adapter.resolve(item, { signal: controller.signal });
    } catch {
      selected = item;
    }
    if (resolution.current === controller) onSelect(selected);
  };
  return (
    <div className="ndf-combo">
      <Input
        placeholder="Av. Apoquindo 4501"
        value={value}
        error={error}
        autoComplete="off"
        id={id}
        role="combobox"
        // With an id the row's <label> names it; alone it names itself.
        aria-label={id ? undefined : 'Dirección'}
        aria-describedby={describedBy}
        // Only when given: Input computes aria-invalid from `error` and an
        // explicit undefined would erase it.
        {...(ariaInvalid != null && { 'aria-invalid': ariaInvalid })}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? `${listId}-list` : undefined}
        aria-activedescendant={
          open && active >= 0 ? `${listId}-${active}` : undefined
        }
        onChange={(event) => {
          resolution.current?.abort();
          resolution.current = null;
          setHasEdited(true);
          setFocused(true);
          onChange(event.target.value);
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          setHasEdited(false);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            setFocused(false);
          }
          if (event.key === 'Tab') {
            setFocused(false);
            setHasEdited(false);
          }
          if (!open && event.key === 'ArrowDown') {
            event.preventDefault();
            setFocused(true);
          }
          if (open && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
            event.preventDefault();
            setActive((index) =>
              event.key === 'ArrowDown'
                ? (index + 1) % items.length
                : index < 0
                  ? items.length - 1
                  : (index - 1 + items.length) % items.length,
            );
          }
          if (open && event.key === 'Enter' && active >= 0) {
            event.preventDefault();
            select(items[active]);
          }
        }}
      />
      {open && (
        <>
          <div className="ndf-menu">
            <div
              id={`${listId}-list`}
              role="listbox"
              aria-label="Direcciones sugeridas"
            >
              {items.map((item, index) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={index === active}
                  id={`${listId}-${index}`}
                  key={item.id}
                  className="ndf-opt"
                  tabIndex={-1}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    select(item);
                  }}
                >
                  <b>{item.label}</b>
                </button>
              ))}
            </div>
            {adapter.attribution && (
              <div
                className="ndf-opt-sub"
                onMouseDown={(event) => event.preventDefault()}
              >
                <a
                  href={adapter.attribution.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {adapter.attribution.label}
                </a>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
AddressAutocomplete.propTypes = {
  adapter: PropTypes.shape({
    suggest: PropTypes.func.isRequired,
    resolve: PropTypes.func,
    reset: PropTypes.func,
    attribution: PropTypes.shape({
      label: PropTypes.string,
      url: PropTypes.string,
    }),
  }).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  error: PropTypes.bool,
  id: PropTypes.string,
  'aria-describedby': PropTypes.string,
  'aria-invalid': PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

// InputRow labels the first element marked as a form control.
AddressAutocomplete.formControl = true;
